import { useMutation } from '@tanstack/react-query';

import useError from '@/hooks/useError';
import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';
import { queryClient } from '@/lib/react-query';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

interface IBatchAssociatePartnersPayload {
  parentPartnerId: string;
  partnerIds: string[];
}

const batchAssociatePartners =
  (makeRequest: ReturnType<typeof useImperativeRequestWrapper>[1]) =>
  async ({ parentPartnerId, partnerIds }: IBatchAssociatePartnersPayload) => {
    const { error, data } = await makeRequest({
      path: `/admin/partners/${parentPartnerId}/associate`,
      method: 'post',
      body: { partner_ids: partnerIds },
    });

    if (error) {
      throw new Error(
        typeof error === 'string' ? error : (error?.message ?? 'Unknown error')
      );
    }

    return data;
  };

export const useCreateBatchAssociatePartners = () => {
  const [, makeRequest] = useImperativeRequestWrapper('lpVillage');
  const addError = useError();
  const addSuccessMsg = useToasts((state) => state.addItem);

  return useMutation({
    onError: () => {
      addError('Failed to link partners');
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['partner-hierarchy'],
      });

      addSuccessMsg(
        {
          msg: 'Partners were successfully linked!',
          type: MessageType.Success,
        },
        'root',
        {}
      );
    },
    mutationFn: batchAssociatePartners(makeRequest),
  });
};
